import { Box, ButtonBase, Chip, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { TreeLine } from "src/components/common/TreeLine";
import { DeployedPlanGridRow } from "./deployedPlanDays";

const TREE_LINE_LEFT = 17;

interface Props {
  row: DeployedPlanGridRow;
  isFiltered: boolean;
  tabIndex: 0 | -1;
  onToggle: (deployedOn: string) => void;
}

export const DeployedOnCell = ({
  row,
  isFiltered,
  tabIndex,
  onToggle,
}: Props) => {
  const { t } = useTenantTranslation();

  if (row.day === null) {
    return (
      <TreeLine isLast={row.isLastInDay} left={TREE_LINE_LEFT} width="100%" />
    );
  }

  const { deployedOn, planCount, listedPlanCount, isOpen } = row.day;
  const deployedOnDate = dayjs(deployedOn);
  const showsSomePlans = isFiltered && listedPlanCount !== planCount;
  const canOpen = listedPlanCount > 1;

  const dayLabel = (
    <>
      {canOpen ? (
        <ChevronRightIcon
          fontSize="small"
          sx={{
            transition: "transform 120ms",
            transform: isOpen ? "rotate(90deg)" : "none",
          }}
        />
      ) : (
        <Box sx={{ width: 20, flex: "none" }} />
      )}
      <Typography
        variant="body1"
        sx={{ fontWeight: "fontWeightBold", whiteSpace: "nowrap" }}
      >
        {deployedOnDate.format("ddd, ll")}
      </Typography>
      {planCount > 1 ? (
        <Chip
          size="small"
          sx={{ ml: "auto" }}
          label={
            showsSomePlans
              ? t("matchingOfTotal", {
                  matching: listedPlanCount,
                  total: planCount,
                })
              : planCount
          }
        />
      ) : null}
    </>
  );
  const date = deployedOnDate.format("LL");
  const dayLabelSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    height: "100%",
    width: "100%",
    justifyContent: "flex-start",
  } as const;

  return (
    <Tooltip
      title={
        showsSomePlans
          ? t("deployedOnMatchTooltip", {
              matching: listedPlanCount,
              total: planCount,
              date,
            })
          : t("deployedOnTooltip", { plans: planCount, date })
      }
    >
      {canOpen ? (
        <ButtonBase
          tabIndex={tabIndex}
          aria-expanded={isOpen}
          onClick={() => {
            onToggle(deployedOn);
          }}
          sx={dayLabelSx}
        >
          {dayLabel}
        </ButtonBase>
      ) : (
        <Box sx={dayLabelSx}>{dayLabel}</Box>
      )}
    </Tooltip>
  );
};
